import { Controller } from "@hotwired/stimulus"
import mapboxgl from 'mapbox-gl'

const STATUS_COLORS = {
  green: "#4ADB54",
  yellow: "#E08C4C",
  red: "#E04C4C",
  none: "#9CA3AF"
}

const STATUS_LABELS = {
  green: "Low risk",
  yellow: "Moderate risk",
  red: "High risk",
  none: "—"
}

// Connects to data-controller="access-report-map"
export default class extends Controller {
  static values = {
    accessToken: String,
    geojson: String,
    latitude: Number,
    longitude: Number
  }
  static targets = ["map"]

  connect() {
    if (!this.hasMapTarget) return

    mapboxgl.accessToken = this.accessTokenValue

    try {
      this.map = new mapboxgl.Map({
        container: this.mapTarget,
        style: "mapbox://styles/mapbox/light-v11",
        center: [this.longitudeValue || -83.7487, this.latitudeValue || 42.2658],
        zoom: 10,
        attributionControl: false
      })

      this.map.on("load", () => this.onMapLoad())
    } catch (error) {
      console.error("Mapbox initialization failed:", error)
    }

    // Listen for provider selection from access-report controller
    this.boundOnProviderSelected = (e) => {
      const { npi, lat, lng } = e.detail
      this.highlightProvider(npi)
      if (lat && lng && this.map) {
        this.map.easeTo({ center: [lng, lat], zoom: Math.max(this.map.getZoom(), 13) })
      }
    }
    document.addEventListener("access-report:providerSelected", this.boundOnProviderSelected)

    // Listen for type filter changes from chip clicks
    this.boundOnTypeFilterChanged = (e) => {
      this.filterByType(e.detail.type)
    }
    document.addEventListener("access-report:typeFilterChanged", this.boundOnTypeFilterChanged)
  }

  onMapLoad() {
    if (!this.geojsonValue) return

    let geojson
    try {
      geojson = JSON.parse(this.geojsonValue)
    } catch (e) {
      console.error("Failed to parse GeoJSON:", e)
      return
    }

    if (!geojson || !geojson.features || geojson.features.length === 0) return

    // Add GeoJSON source — no clustering, show all individual pins
    this.map.addSource("providers", {
      type: "geojson",
      data: geojson
    })

    // Individual provider dots
    this.map.addLayer({
      id: "provider-points",
      type: "circle",
      source: "providers",
      paint: {
        "circle-color": [
          "match", ["get", "status"],
          "green", STATUS_COLORS.green,
          "yellow", STATUS_COLORS.yellow,
          "red", STATUS_COLORS.red,
          STATUS_COLORS.none
        ],
        "circle-radius": 5,
        "circle-stroke-width": 1,
        "circle-stroke-color": "#ffffff"
      }
    })

    // Click individual point
    this.map.on("click", "provider-points", (e) => {
      const npi = e.features[0].properties.id
      if (npi) {
        this.highlightProvider(String(npi))
        document.dispatchEvent(new CustomEvent("access-report-map:providerClick", {
          detail: { npi: String(npi) }
        }))
      }
    })

    // Hover flyout popup
    this.popup = new mapboxgl.Popup({
      closeButton: false,
      closeOnClick: false,
      offset: 12,
      maxWidth: "260px"
    })

    this.map.on("mouseenter", "provider-points", (e) => {
      this.map.getCanvas().style.cursor = "pointer"
      const props = e.features[0].properties
      const status = props.status || "none"
      const statusLabel = STATUS_LABELS[status] || "—"
      const statusColor = STATUS_COLORS[status] || STATUS_COLORS.none
      const name = props.name || "Unknown"
      const specialty = props.specialty || props.providerType || ""
      const networked = props.networked === true || props.networked === "true"
      const is501r = props.is501r === true || props.is501r === "true"
      // One pin per EIN group, so say how many NPIs it stands for. Only grouped
      // features carry the property (see NavigationAccessService#build_geojson).
      const groupCount = parseInt(props.groupCount, 10) || 1

      let html = `<div style="font-family: var(--f-sans, system-ui, sans-serif); padding: 4px 0;">`
      html += `<div style="font-size: 13px; font-weight: 600; color: #1E2046; margin-bottom: 4px;">${this._escapePopupHtml(name)}</div>`
      if (specialty) {
        html += `<div style="font-size: 11px; color: #717399; margin-bottom: 6px;">${this._escapePopupHtml(specialty)}</div>`
      }
      html += `<div style="display: flex; gap: 4px; flex-wrap: wrap;">`
      html += `<span style="display: inline-flex; align-items: center; gap: 4px; font-size: 11px; font-weight: 600; padding: 2px 8px; border-radius: 6px; background: ${statusColor}1a; color: ${statusColor};">`
      html += `<span style="width: 6px; height: 6px; border-radius: 50%; background: ${statusColor};"></span>${statusLabel}</span>`
      if (networked) {
        html += `<span style="font-size: 11px; font-weight: 600; padding: 2px 8px; border-radius: 6px; background: #4C56E01a; color: #4C56E0;">Networked</span>`
      }
      if (is501r) {
        html += `<span style="font-size: 11px; font-weight: 600; padding: 2px 8px; border-radius: 6px; background: #4ADB541a; color: #2EAD6B;">501(r)</span>`
      }
      html += `</div>`
      if (groupCount > 1) {
        html += `<div style="font-size: 11px; color: #717399; margin-top: 6px;">+${groupCount - 1} related NPI${groupCount > 2 ? "s" : ""} at this provider</div>`
      }
      html += `</div>`

      this.popup.setLngLat(e.features[0].geometry.coordinates.slice()).setHTML(html).addTo(this.map)
    })

    this.map.on("mouseleave", "provider-points", () => {
      this.map.getCanvas().style.cursor = ""
      this.popup.remove()
    })

    // Apply any pending type filter that arrived before map loaded
    if (this.pendingTypeFilter) {
      this.filterByType(this.pendingTypeFilter)
      this.pendingTypeFilter = null
    }
  }

  highlightProvider(npi) {
    if (!this.map || !this.map.getSource("providers")) return

    if (this.map.getLayer("highlight-point")) {
      this.map.setFilter("highlight-point", ["==", ["get", "id"], npi])
    } else {
      this.map.addLayer({
        id: "highlight-point",
        type: "circle",
        source: "providers",
        filter: ["==", ["get", "id"], npi],
        paint: {
          "circle-radius": 12,
          "circle-color": "transparent",
          "circle-stroke-width": 3,
          "circle-stroke-color": "#4C56E0"
        }
      })
    }
  }

  clearHighlight() {
    if (!this.map) return
    if (this.map.getLayer("highlight-point")) {
      this.map.setFilter("highlight-point", ["==", ["get", "id"], ""])
    }
  }

  _escapePopupHtml(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }

  filterByType(type) {
    if (!this.map || !this.map.getLayer("provider-points")) {
      this.pendingTypeFilter = type
      return
    }

    this.clearHighlight()

    if (type === "all") {
      this.map.setFilter("provider-points", null)
    } else {
      this.map.setFilter("provider-points", ["==", ["get", "providerType"], type])
    }
  }

  disconnect() {
    document.removeEventListener("access-report:providerSelected", this.boundOnProviderSelected)
    document.removeEventListener("access-report:typeFilterChanged", this.boundOnTypeFilterChanged)
    if (this.popup) this.popup.remove()
    if (this.map) {
      this.map.remove()
      this.map = null
    }
  }
}
